<template>
  <div v-if="show" :class="$style.container">
    <div
      data-pp-message
      :data-pp-buyerCountry="runConfig.country"
      :data-pp-amount="amount"
      :data-pp-placement="placement"
      :data-pp-style-text-size="textSize"
      data-testid="paypal-paylater"
    ></div>
  </div>
</template>

<script>
import Vue from "vue";

import axios from "axios";
import LoadScript from "vue-plugin-load-script";

Vue.use(LoadScript);

export default {
  name: "NsPayPalPayLater",
  props: {
    amount: {
      type: Number,
      required: true
    },

    /**
     * Where the PayPal component is being shown.
     * Valid values are: "home", "category", "product", "cart", "payment"
     */
    placement: {
      type: String,
      required: true
    },

    /**
     * The text size used within the PayPal component.
     * Valid values are: 10, 11, 12, 13, 14, 15, 16
     */
    textSize: {
      type: Number,
      default: 14
    }
  },
  data() {
    return {
      buyerCountry: "",
      payPalClientId: ""
    };
  },
  computed: {
    runConfig() {
      return this.$NsProductAppService.runConfig;
    },

    marketConfig() {
      return this.$NsProductAppService.marketConfig;
    },

    payPalComponentsSrc() {
      return `https://www.paypal.com/sdk/js?client-id=${this.payPalClientId}&components=messages`;
    },

    show() {
      return !!this.runConfig && !!this.payPalClientId && !!this.amount;
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadPayPalComponents();
        }
      }
    }
  },
  async mounted() {
    await this.init();
    this.addListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    async init() {
      this.payPalClientId = await this.getPayPalClientId();
    },

    /**
     * Used to retrieve the PayPal ClientId for the current Market.
     */
    async getPayPalClientId() {
      try {
        const lambdaUrl = `${this.marketConfig.awsUrl}/PaymentPaypal/v1/client-id/${this.runConfig.country}`;
        const lambdaResponse = await axios.get(lambdaUrl, {
          headers: this.getRequestHeaders()
        });
        const lambdaData = this.processLambdaResponse(lambdaResponse);

        const payPalClientId = lambdaData.clientId || "";
        return payPalClientId;
      } catch (error) {
        // getting the clientId failed, component shouldn't show
      }
      return "";
    },

    processLambdaResponse(lambdaResponse) {
      if (!lambdaResponse || !lambdaResponse.data) {
        return {};
      }

      let lambdaData = lambdaResponse.data;
      if (lambdaData.data) {
        // the actual response is nested in another data for now
        lambdaData = lambdaData.data;
      }

      return lambdaData;
    },

    /**
     * Returns headers required to call lambda
     * @returns {{Content-Type: string, client_id: string, client_secret: string}}
     * @private
     */
    getRequestHeaders() {
      return {
        "Content-Type": "application/json",
        client_id: this.marketConfig.checkout.clientId,
        client_secret: this.marketConfig.checkout.clientSecret
      };
    },

    loadPayPalComponents() {
      this.$loadScript(this.payPalComponentsSrc)
        .then(() => {})
        .catch(() => {
          // failed to load PayPal components
        });
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // watch app service finished loading
      this.$watch(
        function() {
          return this.$NsProductAppService.loading;
        },
        async () => {
          await this.init();
        }
      );

      // listen to config changes
      this.$NsProductAppService.$on("config-changed", async () => {
        await this.init();
      });
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to product app service events
      this.$NsProductAppService.$off();
    }
  }
};
</script>

<style lang="scss" module>
* {
  box-sizing: border-box;
}

.container {
  position: relative;
  width: 100%;
}
</style>
